import React, { useState, useEffect } from 'react';
import { Box, Grid, TextField, Typography, Button, Alert, FormControlLabel } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import FormGroup from "@mui/material/FormGroup";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Switch from "@mui/material/Switch";
import Select from '@mui/material/Select';
import { useMember } from '../context/MemberProfileContext';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import { updateHouseholdMembers } from '../api/memberServices';

const amber = '#B8860A';
const navy = '#1A3A6B';
const pageBg = '#f8f7f2';

const Section = ({ title, children }) => (
  <Box sx={{ border: '1px solid #c8d4de', borderRadius: 1.5, mb: 3, overflow: 'hidden' }}>
    <Box sx={{ bgcolor: '#e4eaf0', borderBottom: '1px solid #c8d4de', px: 3, py: 1.5 }}>
      <Typography sx={{ color: navy, fontWeight: 700, fontSize: '1rem', fontFamily: '"Roboto","Helvetica","Arial",sans-serif', textAlign: 'left' }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 3 }}>
      {children}
    </Box>
  </Box>
);

const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
  ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
  ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
  ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
  ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];

function HouseholdMembers() {
  const { memberProfile, setMemberProfile, personName } = useMember();
  const [formState, setFormState] = useState({
    ...memberProfile,
    spouse: memberProfile.spouse || {},
  });
  const [studentFields, setStudentFields] = useState(memberProfile.children);
  const [addPeople, setAddPeople] = useState(0);
  const [addSpouse, setAddSpouse] = useState('');
  const { accessToken, signOut, handleLogout } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [genders] = useState(['Male', 'Female']);
  const [isEditMode, setIsEditMode] = useState(false);
  const [addressModified, setAddressModified] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    setFormState(memberProfile);
  }, []);

  const handleInputChange = (section, field, value) => {
    setFormState((prevState) => ({
      ...prevState,
      [section]: { ...prevState[section], [field]: value },
    }));
  };

  const checkoutHandler = (e) => {
    e.preventDefault();
    handleSave();
  };

  const handleSave = async () => {
    if (formState.primary.email === formState.spouse.email) {
      setError('Parent and spouse email addresses cannot be the same.');
      return;
    }
    if (formState.primary.mobileNumber === formState.spouse.mobileNumber) {
      setError('Parent and spouse mobile numbers cannot be the same.');
      return;
    }
    const mobileNumberRegex = /^\d+$/;
    if (
      (formState.primary.mobileNumber && !mobileNumberRegex.test(formState.primary.mobileNumber)) ||
      (formState.spouse.mobileNumber && !mobileNumberRegex.test(formState.spouse.mobileNumber))
    ) {
      setError('Mobile numbers can only contain digits.');
      return;
    }
    try {
      const { data, response } = await updateHouseholdMembers(accessToken, formState);
      if (response.ok) {
        setSuccess('Household information changed successfully!');
        navigate('/EventTypesForm');
        setError('');
      } else {
        if (response.status === 400)       setError(data.message);
        else if (response.status === 401)  setError('Unauthorized. Please log in again.');
        else if (response.status === 403)  { setError('Access token expired. Please log in again.'); signOut(); navigate('/'); }
        else if (response.status === 500)  setError('Server error. Please try again later.');
        else                               setError('Failed to change Household data.');
      }
    } catch (err) {
      setError('An error occurred. Please try again. ' + err);
    }
  };

  function peopleSwitchHandler(e) {
    handleAddFields();
    setAddPeople(e.target.checked);
  }
  function spouseSwitchHandler(e) {
    setAddSpouse(e.target.checked);
  }
  const handleEditToggle = () => {
    setIsEditMode(!isEditMode);
    setAddressModified(true);
    formState.addressModified = true;
  };
  const handleCancel = () => navigate('/EventTypesForm');

  const handleStudentChange = (e, index) => {
    let newForm = [...studentFields];
    newForm[index][e.target.name] = e.target.value;
    setStudentFields(newForm);
    formState.children = newForm;
    memberProfile.children = newForm;
  };
  const handleAddFields = () => {
    setStudentFields([...studentFields, {
      memberType: 'child', firstName: '', lastName: '', gender: '', email: '', mobileNumber: '',
    }]);
  };
  const handleRemoveFields = (index) => {
    const values = [...studentFields];
    values.splice(index, 1);
    setStudentFields(values);
    formState.children = values;
    memberProfile.children = values;
  };

  return (
    <Box sx={{ bgcolor: pageBg, minHeight: '100vh', py: 4, px: { xs: 2, md: 4 }, display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '100%', maxWidth: 860, bgcolor: '#fff', borderRadius: 2, boxShadow: 3, overflow: 'hidden', alignSelf: 'flex-start' }}>

        {/* Page header */}
        <Box sx={{ bgcolor: navy, px: { xs: 3, md: 5 }, py: 3 }}>
          <Typography variant="h5" sx={{ color: '#fff', fontFamily: 'Georgia, serif', fontWeight: 700, letterSpacing: 0.5 }}>
            Household Members
          </Typography>
          <Typography variant="body2" sx={{ color: '#c5d8ea', mt: 0.5 }}>
            CMDFW Registration System
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
          <form onSubmit={checkoutHandler}>

            {/* Parent Information */}
            <Section title="Parent Information">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="First Name" name="firstName"
                    value={formState.primary.firstName}
                    onChange={(e) => handleInputChange('primary', 'firstName', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth required label="Last Name" name="lastName"
                    value={formState.primary.lastName}
                    onChange={(e) => handleInputChange('primary', 'lastName', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth disabled label="Mobile Number"
                    value={formState.primary.mobileNumber}
                    onChange={(e) => handleInputChange('primary', 'mobileNumber', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth disabled label="Email"
                    value={formState.primary.email}
                    onChange={(e) => handleInputChange('primary', 'email', e.target.value)} />
                </Grid>
              </Grid>
            </Section>

            {/* Spouse Information */}
            <Section title="Spouse Information">
              {!formState.spouse.firstName && !formState.spouse.lastName && (
                <FormGroup sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={<Switch onChange={spouseSwitchHandler}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: amber },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: amber } }} />}
                    label="Add Spouse (Optional)"
                  />
                </FormGroup>
              )}
              {(addSpouse || formState.spouse.firstName || formState.spouse.lastName) && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required label="First Name" name="firstName"
                      value={formState.spouse.firstName}
                      onChange={(e) => handleInputChange('spouse', 'firstName', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required label="Last Name" name="lastName"
                      value={formState.spouse.lastName}
                      onChange={(e) => handleInputChange('spouse', 'lastName', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required label="Mobile Number" type="number" name="mobileNumber"
                      value={formState.spouse.mobileNumber}
                      onChange={(e) => handleInputChange('spouse', 'mobileNumber', e.target.value)}
                      disabled={formState.spouse.status === 'ACTIVE'} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required label="Email" type="email" name="email"
                      value={formState.spouse.email}
                      onChange={(e) => handleInputChange('spouse', 'email', e.target.value)}
                      disabled={formState.spouse.status === 'ACTIVE'} />
                  </Grid>
                </Grid>
              )}
            </Section>

            {/* Children */}
            <Section title="Balavihar Children">
              {studentFields.length === 0 && (
                <FormGroup sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={<Switch onChange={peopleSwitchHandler}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: amber },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: amber } }} />}
                    label="Add Balavihar Children"
                  />
                </FormGroup>
              )}

              {studentFields && studentFields.map((child, index) => (
                <Box key={index} sx={{ border: '1px solid #dde3ea', borderRadius: 1.5, p: 2.5, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, color: navy, fontSize: '0.95rem' }}>
                      Child {index + 1}
                    </Typography>
                    <Box>
                      <IconButton size="small" onClick={() => handleRemoveFields(index)}
                        sx={{ color: '#c0392b', mr: 0.5 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleAddFields()}
                        sx={{ color: navy }}>
                        <PersonAddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth required label="First Name" name="firstName"
                        value={child.firstName}
                        onChange={(e) => handleStudentChange(e, index)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth required label="Last Name" name="lastName"
                        value={child.lastName}
                        onChange={(e) => handleStudentChange(e, index)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Gender</InputLabel>
                        <Select required name="gender" value={child.gender} label="Gender"
                          onChange={(e) => handleStudentChange(e, index)}>
                          {genders.map((g, i) => <MenuItem key={i} value={g}>{g}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Mobile Number" name="mobileNumber"
                        value={child.mobileNumber}
                        onChange={(e) => handleStudentChange(e, index)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Email" type="email" name="email"
                        value={child.email}
                        onChange={(e) => handleStudentChange(e, index)} />
                    </Grid>
                  </Grid>
                </Box>
              ))}

              {studentFields.length > 0 && (
                <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={handleAddFields}
                  sx={{ borderColor: navy, color: navy, mt: 1 }}>
                  Add Another Child
                </Button>
              )}
            </Section>

            {/* Household Address */}
            <Section title="Household Address">
              {isEditMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField fullWidth required label="Address Line 1" name="streetAddress" variant="outlined"
                      value={formState.houseHold.streetAddress}
                      onChange={(e) => handleInputChange('houseHold', 'streetAddress', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="City" name="city" variant="outlined"
                      value={formState.houseHold.city}
                      onChange={(e) => handleInputChange('houseHold', 'city', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>State</InputLabel>
                      <Select required name="state" label="State"
                        value={formState.houseHold.state}
                        onChange={(e) => handleInputChange('houseHold', 'state', e.target.value)}>
                        {US_STATES.map(([val, label]) => (
                          <MenuItem key={val} value={val}>{label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required label="Zip Code" name="postalCode" variant="outlined"
                      value={formState.houseHold.postalCode}
                      onChange={(e) => handleInputChange('houseHold', 'postalCode', e.target.value)} />
                  </Grid>
                </Grid>
              ) : (
                <Box>
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    {formState.houseHold.streetAddress}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {formState.houseHold.city}, {formState.houseHold.state} {formState.houseHold.postalCode}
                  </Typography>
                  <Button variant="outlined" onClick={handleEditToggle}
                    sx={{ borderColor: amber, color: amber, '&:hover': { borderColor: '#9a6f08', color: '#9a6f08' } }}>
                    Modify Address
                  </Button>
                </Box>
              )}
            </Section>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <Button onClick={handleCancel} variant="outlined"
                sx={{ borderColor: '#bbb', color: '#555', px: 3 }}>
                Cancel
              </Button>
              <Button type="submit" variant="contained"
                sx={{ bgcolor: amber, color: '#fff', fontWeight: 700, px: 4,
                  '&:hover': { bgcolor: '#9a6f08' } }}>
                Save
              </Button>
            </Box>

            <Typography variant="caption" sx={{ display: 'block', mt: 3, color: '#999' }}>
              Questions? Contact us at{' '}
              <a href="mailto:membership@cmdfw.org" style={{ color: amber }}>membership@cmdfw.org</a>
            </Typography>

          </form>
        </Box>
      </Box>
    </Box>
  );
}

export default HouseholdMembers;
